import type {
  OfferDiscount,
  OfferVatMode,
  OfferDocumentDraft,
  OfferDraftRow,
  OfferDocumentEditablePara,
  OfferExtraPositionDraft,
  OfferIntroBlock,
  OfferIntroBlockPersisted,
  OfferAttachment,
  OfferAttachmentMimeType,
} from '../../../types';
import { isOfferVatMode } from './offerVat';
import { parsePartialInvoiceMode, roundAbschlagPercent } from './partialInvoiceTotals';
import { parseOfferDocumentPaymentTerms } from './offerDocumentMetaPatch';
import { roundMoney } from './finalInvoiceTotals';
import { normalizeRetention, normalizeSiteLevy } from './invoicePayableTotals';
import { parseOfferLeistungszeitraum } from './offerLeistungszeitraum';
import { parseExcludedStandardAttachments } from './offerLegalAttachments';
import { parseCurrentIssuedInvoiceByDocType } from './finalInvoiceDeduction';
import { normalizeCalculationDismissedHints } from './offerMarginHints';
import {
  clampInlineImagesAcrossZones,
  isOfferDocumentContentBlock,
  normalizeOfferDocumentContent,
} from './offerDocumentContent';

/** Shared with the API. Large Leistungsverzeichnisse copy every position into the draft. */
export const OFFER_DOCUMENT_DRAFT_MAX_CHARS = 2_000_000;

function isPara(x: unknown): x is OfferDocumentEditablePara {
  return (
    typeof x === 'object' &&
    x !== null &&
    typeof (x as OfferDocumentEditablePara).id === 'string' &&
    typeof (x as OfferDocumentEditablePara).text === 'string'
  );
}

function isIntroBlock(x: unknown): x is OfferIntroBlockPersisted {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  if (typeof o.id !== 'string' || typeof o.type !== 'string') return false;
  if (o.type === 'text' || o.type === 'title') return typeof o.content === 'string';
  if (o.type === 'image') return typeof o.alt === 'string';
  return false;
}

function isExtraPosition(x: unknown): x is OfferExtraPositionDraft {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.item_name === 'string' &&
    typeof o.description === 'string' &&
    typeof o.quantity === 'number' &&
    typeof o.unit === 'string' &&
    typeof o.unit_price_vk === 'number'
  );
}

const ALLOWED_ATTACHMENT_MIMES: ReadonlyArray<OfferAttachmentMimeType> = [
  'application/pdf',
  'image/png',
  'image/jpeg',
];

function isAttachment(x: unknown): x is OfferAttachment {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  if (typeof o.id !== 'string' || !o.id) return false;
  if (typeof o.filename !== 'string' || !o.filename) return false;
  if (typeof o.object_path !== 'string' || !o.object_path) return false;
  if (typeof o.size !== 'number' || !Number.isFinite(o.size)) return false;
  if (typeof o.mime_type !== 'string') return false;
  if (!ALLOWED_ATTACHMENT_MIMES.includes(o.mime_type as OfferAttachmentMimeType)) return false;
  return true;
}

function isDraftRow(x: unknown): x is OfferDraftRow {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  const kind = String(o.kind ?? '');
  if (!['apiPosition', 'text', 'title', 'freePosition', 'image', 'positionSurcharge'].includes(kind)) return false;
  if (typeof o.id !== 'string') return false;
  if (typeof o.quantity !== 'number' || !Number.isFinite(o.quantity)) return false;
  if (typeof o.unit !== 'string') return false;
  if (typeof o.item_name !== 'string') return false;
  if (typeof o.description !== 'string') return false;
  if (o.internal_note != null && typeof o.internal_note !== 'string') return false;
  if (typeof o.unit_price_vk !== 'number' || !Number.isFinite(o.unit_price_vk)) return false;
  if (o.labor_minutes != null && (typeof o.labor_minutes !== 'number' || !Number.isFinite(o.labor_minutes))) {
    return false;
  }
  if (o.image != null) {
    const img = o.image as Record<string, unknown>;
    if (typeof img !== 'object' || typeof img.src !== 'string' || typeof img.alt !== 'string') return false;
    if (img.filename != null && typeof img.filename !== 'string') return false;
  }
  if (o.positionSurcharge != null) {
    const ps = o.positionSurcharge as Record<string, unknown>;
    if (typeof ps !== 'object' || typeof ps.percent !== 'number' || !Number.isFinite(ps.percent)) return false;
    if (!Array.isArray(ps.targetRowIds) || !ps.targetRowIds.every((id) => typeof id === 'string')) return false;
    if (ps.mode != null && ps.mode !== 'percent' && ps.mode !== 'amount') return false;
    if (ps.mode === 'amount') {
      if (typeof ps.amount !== 'number' || !Number.isFinite(ps.amount)) return false;
    } else if (ps.amount != null && (typeof ps.amount !== 'number' || !Number.isFinite(ps.amount))) {
      return false;
    }
  }
  if (o.isEventual != null && typeof o.isEventual !== 'boolean') return false;
  if (o.costKind != null && o.costKind !== 'neutral' && o.costKind !== 'leistung' && o.costKind !== 'material') {
    return false;
  }
  if (o.titleLevel != null) {
    if (typeof o.titleLevel !== 'number' || !Number.isFinite(o.titleLevel)) return false;
    if (o.titleLevel < 1 || o.titleLevel > 4) return false;
  }
  return true;
}

/** Rohdaten aus API (Objekt oder JSON-String) → Entwurf. */
export function parseOfferDocumentDraft(raw: unknown): OfferDocumentDraft | null {
  if (raw == null) return null;
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      obj = JSON.parse(t) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return null;
  const d = obj as Record<string, unknown>;
  const out: OfferDocumentDraft = {};

  if (typeof (d as any).documentType === 'string') out.documentType = String((d as any).documentType);
  if (typeof (d as any).document_type === 'string') out.documentType = String((d as any).document_type);

  if (typeof d.offerTitleRest === 'string') out.offerTitleRest = d.offerTitleRest;
  if (typeof d.hideDocumentTypeInTitle === 'boolean') {
    out.hideDocumentTypeInTitle = d.hideDocumentTypeInTitle;
  }
  if (typeof d.leistungsort === 'string') out.leistungsort = d.leistungsort;
  const lz = parseOfferLeistungszeitraum(d.leistungszeitraum);
  if (lz) out.leistungszeitraum = lz;
  const ld = parseOfferLeistungszeitraum(d.lieferdatum);
  if (ld) out.lieferdatum = ld;
  if (typeof d.hideLeistungszeitraum === 'boolean') out.hideLeistungszeitraum = d.hideLeistungszeitraum;
  if (typeof d.hideLieferdatum === 'boolean') out.hideLieferdatum = d.hideLieferdatum;
  if (typeof d.issueDate === 'string') out.issueDate = d.issueDate;
  if (Array.isArray(d.recipientNameLines)) {
    out.recipientNameLines = d.recipientNameLines.filter((v): v is string => typeof v === 'string');
  }
  if (typeof d.recipientStreetLine === 'string') out.recipientStreetLine = d.recipientStreetLine;
  if (typeof d.recipientPlzOrtLine === 'string') out.recipientPlzOrtLine = d.recipientPlzOrtLine;
  if (typeof d.closingGreeting === 'string') out.closingGreeting = d.closingGreeting;
  if (typeof d.signatureHint === 'string') out.signatureHint = d.signatureHint;
  if (typeof d.version === 'number') out.version = d.version;

  if (Array.isArray(d.introLetterParas) && d.introLetterParas.every(isPara)) {
    out.introLetterParas = d.introLetterParas;
  }
  if (Array.isArray(d.afterTotalsParas) && d.afterTotalsParas.every(isPara)) {
    out.afterTotalsParas = d.afterTotalsParas;
  }
  if (d.paymentTermsCustomized === true) out.paymentTermsCustomized = true;
  if (Array.isArray(d.introBlocks) && d.introBlocks.every(isIntroBlock)) {
    out.introBlocks = d.introBlocks;
  }
  if (Array.isArray(d.closingBlocks) && d.closingBlocks.every(isIntroBlock)) {
    out.closingBlocks = d.closingBlocks;
  }
  if (Array.isArray(d.introContent) && d.introContent.every(isOfferDocumentContentBlock)) {
    out.introContent = d.introContent;
  }
  if (Array.isArray(d.closingContent) && d.closingContent.every(isOfferDocumentContentBlock)) {
    out.closingContent = d.closingContent;
  }
  if (Array.isArray(d.extraPositions) && d.extraPositions.every(isExtraPosition)) {
    out.extraPositions = d.extraPositions;
  }
  if (
    typeof d.discount === 'object' &&
    d.discount !== null &&
    (((d.discount as OfferDiscount).type === 'percent') || ((d.discount as OfferDiscount).type === 'absolute')) &&
    typeof (d.discount as OfferDiscount).value === 'number' &&
    Number.isFinite((d.discount as OfferDiscount).value)
  ) {
    out.discount = d.discount as OfferDiscount;
  }
  const siteLevy = normalizeSiteLevy(d.siteLevy);
  if (siteLevy) out.siteLevy = siteLevy;
  const retention = normalizeRetention(d.retention);
  if (retention) out.retention = retention;
  if (d.constructionDeductionsCustomized === true) out.constructionDeductionsCustomized = true;
  if (typeof d.amountAlreadyInvoiced === 'number' && Number.isFinite(d.amountAlreadyInvoiced)) {
    out.amountAlreadyInvoiced = Math.max(0, d.amountAlreadyInvoiced);
  }
  if (typeof d.abschlagPercent === 'number' && Number.isFinite(d.abschlagPercent)) {
    out.abschlagPercent = roundAbschlagPercent(d.abschlagPercent);
  }
  if (typeof d.partialInvoiceAmount === 'number' && Number.isFinite(d.partialInvoiceAmount)) {
    out.partialInvoiceAmount = roundMoney(Math.max(0, d.partialInvoiceAmount));
  } else if (typeof d.amountAlreadyInvoiced === 'number' && Number.isFinite(d.amountAlreadyInvoiced)) {
    out.partialInvoiceAmount = roundMoney(Math.max(0, d.amountAlreadyInvoiced));
  }
  if (typeof d.partialInvoiceDescription === 'string') {
    out.partialInvoiceDescription = d.partialInvoiceDescription;
  }
  if (d.partialInvoiceAmountIsGross === true) out.partialInvoiceAmountIsGross = true;
  if (typeof d.partialInvoiceLineName === 'string') {
    out.partialInvoiceLineName = d.partialInvoiceLineName;
  }
  const partialMode = parsePartialInvoiceMode(d.partialInvoiceMode);
  if (partialMode) out.partialInvoiceMode = partialMode;
  if (typeof d.partialInvoiceCumulative === 'boolean') out.partialInvoiceCumulative = d.partialInvoiceCumulative;
  if (typeof d.orderTotalNet === 'number' && Number.isFinite(d.orderTotalNet) && d.orderTotalNet >= 0) {
    out.orderTotalNet = roundMoney(d.orderTotalNet);
  }
  const paymentTerms = parseOfferDocumentPaymentTerms(d.paymentTerms);
  if (paymentTerms) out.paymentTerms = paymentTerms;
  if (
    typeof d.positionInvoiceQuantities === 'object' &&
    d.positionInvoiceQuantities !== null &&
    !Array.isArray(d.positionInvoiceQuantities)
  ) {
    const m: Record<string, number> = {};
    for (const [k, v] of Object.entries(d.positionInvoiceQuantities as Record<string, unknown>)) {
      const qty = Number(v);
      if (typeof k === 'string' && k.trim() && Number.isFinite(qty) && qty >= 0) {
        m[k.trim()] = qty;
      }
    }
    if (Object.keys(m).length > 0) out.positionInvoiceQuantities = m;
  }
  if (isOfferVatMode(d.vat_mode)) {
    out.vat_mode = d.vat_mode as OfferVatMode;
  }
  if (typeof (d as { hideTotals?: unknown }).hideTotals === 'boolean') {
    out.hideTotals = (d as { hideTotals: boolean }).hideTotals;
  }
  if (typeof (d as { hideArticleNumber?: unknown }).hideArticleNumber === 'boolean') {
    out.hideArticleNumber = (d as { hideArticleNumber: boolean }).hideArticleNumber;
  }
  if (typeof (d as { hidePositionDescriptions?: unknown }).hidePositionDescriptions === 'boolean') {
    out.hidePositionDescriptions = (d as { hidePositionDescriptions: boolean }).hidePositionDescriptions;
  }
  if (d.positionDetailLevel === 'full' || d.positionDetailLevel === 'titles') {
    out.positionDetailLevel = d.positionDetailLevel;
  }
  if (d.laborCostShare === 'auto' || d.laborCostShare === 'on' || d.laborCostShare === 'off') {
    out.laborCostShare = d.laborCostShare;
  }
  if (
    typeof d.calculationHourlyRate === 'number' &&
    Number.isFinite(d.calculationHourlyRate) &&
    d.calculationHourlyRate >= 0
  ) {
    out.calculationHourlyRate = d.calculationHourlyRate;
  }
  if (typeof d.calculationHourlyRateFromCustomer === 'boolean') {
    out.calculationHourlyRateFromCustomer = d.calculationHourlyRateFromCustomer;
  }
  if (typeof d.calculationLaborSeparately === 'boolean') {
    out.calculationLaborSeparately = d.calculationLaborSeparately;
  }
  if (typeof d.calculationLaborRowLabel === 'string') {
    const label = d.calculationLaborRowLabel.trim().slice(0, 80);
    if (label) out.calculationLaborRowLabel = label;
  }
  if (
    typeof d.calculationLaborRowNames === 'object' &&
    d.calculationLaborRowNames !== null &&
    !Array.isArray(d.calculationLaborRowNames)
  ) {
    const names: Record<string, string> = {};
    for (const [key, raw] of Object.entries(d.calculationLaborRowNames as Record<string, unknown>)) {
      if (!key || typeof raw !== 'string') continue;
      const trimmed = raw.trim().slice(0, 200);
      if (trimmed) names[key] = trimmed;
    }
    if (Object.keys(names).length > 0) out.calculationLaborRowNames = names;
  }
  {
    const dismissedHints = normalizeCalculationDismissedHints(d.calculationDismissedHints);
    if (dismissedHints.length > 0) out.calculationDismissedHints = dismissedHints;
  }
  if (
    typeof d.calculationRealLaborCostRate === 'number' &&
    Number.isFinite(d.calculationRealLaborCostRate) &&
    d.calculationRealLaborCostRate >= 0
  ) {
    out.calculationRealLaborCostRate = d.calculationRealLaborCostRate;
  }
  if (d.calculationCopperQuoteEurPer100kg === null) {
    out.calculationCopperQuoteEurPer100kg = null;
  } else if (
    typeof d.calculationCopperQuoteEurPer100kg === 'number' &&
    Number.isFinite(d.calculationCopperQuoteEurPer100kg) &&
    d.calculationCopperQuoteEurPer100kg >= 0
  ) {
    out.calculationCopperQuoteEurPer100kg = d.calculationCopperQuoteEurPer100kg;
  }
  if (d.calculationAluminumQuoteEurPer100kg === null) {
    out.calculationAluminumQuoteEurPer100kg = null;
  } else if (
    typeof d.calculationAluminumQuoteEurPer100kg === 'number' &&
    Number.isFinite(d.calculationAluminumQuoteEurPer100kg) &&
    d.calculationAluminumQuoteEurPer100kg >= 0
  ) {
    out.calculationAluminumQuoteEurPer100kg = d.calculationAluminumQuoteEurPer100kg;
  }
  if (
    d.calculationMetalQuoteSource === 'manual' ||
    d.calculationMetalQuoteSource === 'westmetall' ||
    d.calculationMetalQuoteSource === 'sonepar'
  ) {
    out.calculationMetalQuoteSource = d.calculationMetalQuoteSource;
  }
  if (d.calculationMetalQuoteDate === null) {
    out.calculationMetalQuoteDate = null;
  } else if (
    typeof d.calculationMetalQuoteDate === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(d.calculationMetalQuoteDate)
  ) {
    out.calculationMetalQuoteDate = d.calculationMetalQuoteDate;
  }
  if (
    typeof d.calculationMetalProcurementCostPercent === 'number' &&
    Number.isFinite(d.calculationMetalProcurementCostPercent) &&
    d.calculationMetalProcurementCostPercent >= 0 &&
    d.calculationMetalProcurementCostPercent <= 100
  ) {
    out.calculationMetalProcurementCostPercent =
      d.calculationMetalProcurementCostPercent;
  }
  if (Array.isArray(d.selectedDeductionInvoiceIds)) {
    out.selectedDeductionInvoiceIds = d.selectedDeductionInvoiceIds.filter(
      (v): v is string => typeof v === 'string' && v.trim().length > 0,
    );
  }
  {
    const issued = parseCurrentIssuedInvoiceByDocType(d.currentIssuedInvoiceByDocType);
    if (issued) out.currentIssuedInvoiceByDocType = issued;
  }

  if (
    typeof d.export_serial_by_doc_type === 'object' &&
    d.export_serial_by_doc_type !== null &&
    !Array.isArray(d.export_serial_by_doc_type)
  ) {
    const m: Record<string, number> = {};
    for (const [k, v] of Object.entries(d.export_serial_by_doc_type as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
        m[k] = Math.floor(v);
      }
    }
    if (Object.keys(m).length > 0) out.export_serial_by_doc_type = m;
  }

  if (
    typeof d.export_reference_by_doc_type === 'object' &&
    d.export_reference_by_doc_type !== null &&
    !Array.isArray(d.export_reference_by_doc_type)
  ) {
    const m: Record<string, string> = {};
    for (const [k, v] of Object.entries(d.export_reference_by_doc_type as Record<string, unknown>)) {
      if (typeof v === 'string' && v.trim()) {
        m[k] = v.trim();
      }
    }
    if (Object.keys(m).length > 0) out.export_reference_by_doc_type = m;
  }

  if (Array.isArray(d.attachments) && d.attachments.every(isAttachment)) {
    out.attachments = d.attachments;
  }
  if (Array.isArray(d.excluded_standard_attachments)) {
    const excluded = parseExcludedStandardAttachments(d.excluded_standard_attachments);
    if (excluded.length > 0) out.excluded_standard_attachments = excluded;
  }

  if (Array.isArray(d.draftRows) && d.draftRows.every(isDraftRow)) {
    out.draftRows = d.draftRows;
  } else if (Array.isArray(d.extraPositions) && d.extraPositions.every(isExtraPosition)) {
    // Legacy fallback: migrate extra rows into the new row model.
    out.draftRows = d.extraPositions.map((r) => ({
      id: r.id,
      kind: 'freePosition' as const,
      quantity: Number.isFinite(r.quantity) ? r.quantity : 1,
      unit: r.unit || 'Stk',
      item_name: r.item_name || '',
      description: r.description || '',
      internal_note: '',
      unit_price_vk: Number.isFinite(r.unit_price_vk) ? r.unit_price_vk : 0,
      unit_cost_ek: 0,
      surcharge_percent: 0,
      labor_minutes: 0,
      image: null,
    }));
  }

  if (
    out.offerTitleRest == null &&
    out.hideDocumentTypeInTitle == null &&
    out.documentType == null &&
    out.leistungsort == null &&
    out.leistungszeitraum == null &&
    out.lieferdatum == null &&
    out.hideLeistungszeitraum == null &&
    out.hideLieferdatum == null &&
    out.issueDate == null &&
    out.recipientNameLines == null &&
    out.recipientStreetLine == null &&
    out.recipientPlzOrtLine == null &&
    out.introLetterParas == null &&
    out.afterTotalsParas == null &&
    out.paymentTermsCustomized == null &&
    out.closingGreeting == null &&
    out.signatureHint == null &&
    out.introBlocks == null &&
    out.closingBlocks == null &&
    out.introContent == null &&
    out.closingContent == null &&
    out.extraPositions == null &&
    out.draftRows == null &&
    out.discount == null &&
    out.siteLevy == null &&
    out.retention == null &&
    out.constructionDeductionsCustomized == null &&
    out.amountAlreadyInvoiced == null &&
    out.abschlagPercent == null &&
    out.partialInvoiceAmount == null &&
    out.partialInvoiceDescription == null &&
    out.partialInvoiceLineName == null &&
    out.partialInvoiceAmountIsGross == null &&
    out.partialInvoiceMode == null &&
    out.partialInvoiceCumulative == null &&
    out.orderTotalNet == null &&
    out.paymentTerms == null &&
    out.positionInvoiceQuantities == null &&
    out.vat_mode == null &&
    out.hideTotals == null &&
    out.hideArticleNumber == null &&
    out.hidePositionDescriptions == null &&
    out.positionDetailLevel == null &&
    out.laborCostShare == null &&
    out.calculationHourlyRate == null &&
    out.calculationHourlyRateFromCustomer == null &&
    out.calculationLaborSeparately == null &&
    out.calculationLaborRowLabel == null &&
    out.calculationLaborRowNames == null &&
    out.calculationDismissedHints == null &&
    out.calculationRealLaborCostRate == null &&
    out.calculationCopperQuoteEurPer100kg === undefined &&
    out.calculationAluminumQuoteEurPer100kg === undefined &&
    out.calculationMetalQuoteSource === undefined &&
    out.calculationMetalQuoteDate === undefined &&
    out.calculationMetalProcurementCostPercent === undefined &&
    out.export_serial_by_doc_type == null &&
    out.export_reference_by_doc_type == null &&
    out.currentIssuedInvoiceByDocType == null &&
    out.attachments == null &&
    out.excluded_standard_attachments == null &&
    out.selectedDeductionInvoiceIds == null
  ) {
    return null;
  }
  return out;
}

/**
 * Nur Rabatt und USt-Modus für schnelle Summenberechnung (Listenansicht),
 * ohne vollständige Draft-Validierung oder große Arrays zu traversieren.
 */
export function extractOfferDraftPricingHints(raw: unknown): {
  vat_mode?: OfferVatMode;
  discount?: OfferDiscount;
} {
  if (raw == null) return {};
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return {};
    try {
      obj = JSON.parse(t) as unknown;
    } catch {
      return {};
    }
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return {};
  const d = obj as Record<string, unknown>;
  const out: { vat_mode?: OfferVatMode; discount?: OfferDiscount } = {};
  if (isOfferVatMode(d.vat_mode)) {
    out.vat_mode = d.vat_mode;
  }
  const disc = d.discount;
  if (
    typeof disc === 'object' &&
    disc !== null &&
    (String((disc as { type?: unknown }).type) === 'percent' ||
      String((disc as { type?: unknown }).type) === 'absolute') &&
    typeof (disc as { value?: unknown }).value === 'number' &&
    Number.isFinite((disc as { value: number }).value)
  ) {
    const type = String((disc as { type: string }).type) === 'absolute' ? 'absolute' : 'percent';
    const value = (disc as { value: number }).value;
    if (value > 0) {
      out.discount = { type, value };
    }
  }
  return out;
}

/** Bilder ohne Data-URL für SQLite; nur Metadaten behalten. */
export function sanitizeOfferDocumentDraftForSave(draft: OfferDocumentDraft): OfferDocumentDraft {
  const stripBlocks = (blocks: OfferIntroBlockPersisted[] | undefined) =>
    blocks?.map((b) => {
      if (b.type === 'image') {
        return { id: b.id, type: 'image' as const, alt: b.alt };
      }
      return b;
    });

  const clamped = clampInlineImagesAcrossZones(
    normalizeOfferDocumentContent({
      content: draft.introContent,
      paras: draft.introLetterParas,
      blocks: draft.introBlocks,
    }),
    normalizeOfferDocumentContent({
      content: draft.closingContent,
      paras: draft.afterTotalsParas,
      blocks: draft.closingBlocks,
    }),
  );

  return {
    ...draft,
    version: 1,
    introContent: clamped.intro,
    closingContent: clamped.closing,
    introBlocks: stripBlocks(draft.introBlocks),
    closingBlocks: stripBlocks(draft.closingBlocks),
    draftRows: draft.draftRows,
    attachments: draft.attachments,
    excluded_standard_attachments: draft.excluded_standard_attachments,
  };
}

export function assertDraftJsonSize(draft: OfferDocumentDraft): void {
  const s = JSON.stringify(draft);
  if (s.length > OFFER_DOCUMENT_DRAFT_MAX_CHARS) {
    throw new Error('Angebotsentwurf ist zu groß zum Speichern (z. B. sehr große Bilder). Bitte Bild-Bausteine entfernen oder verkleinern.');
  }
}

/**
 * Drops draft rows linked to deleted catalog positions and removes those row ids
 * from surcharge targets. Prices stay as stored; the editor reconciles them next.
 */
export function omitDraftRowsForDeletedPositions(
  rows: OfferDraftRow[],
  positionIds: Iterable<string>,
): OfferDraftRow[] {
  const ids = new Set(Array.from(positionIds, (id) => String(id)));
  if (ids.size === 0) return rows;
  const removedRowIds = new Set(
    rows.filter((row) => ids.has(String(row.leadPositionId ?? ''))).map((row) => row.id),
  );
  if (removedRowIds.size === 0) return rows;
  return rows
    .filter((row) => !ids.has(String(row.leadPositionId ?? '')))
    .map((row) => {
      if (row.kind !== 'positionSurcharge' || !row.positionSurcharge) return row;
      const targetRowIds = row.positionSurcharge.targetRowIds.filter((id) => !removedRowIds.has(id));
      if (targetRowIds.length === row.positionSurcharge.targetRowIds.length) return row;
      return { ...row, positionSurcharge: { ...row.positionSurcharge, targetRowIds } };
    });
}

/** Persistierte Blöcke für den Editor (Bild ohne src → leer). */
export function hydrateIntroBlocksFromDraft(blocks: OfferIntroBlockPersisted[] | undefined): OfferIntroBlock[] {
  if (!blocks?.length) return [];
  return blocks.map((b) => {
    if (b.type === 'image') {
      return { id: b.id, type: 'image', src: '', alt: b.alt };
    }
    if (b.type === 'title') {
      return { id: b.id, type: 'title', content: b.content };
    }
    return { id: b.id, type: 'text', content: b.content };
  });
}
